import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Grid } from "@mui/material";
import { PageContainer, SectionContainer } from "@answersnow/pkg-theme";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Button from "@mui/material/Button";
import actions from "actions";
import { getVideoCallChatMessages } from "api/video";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { prepareChimeAttachment } from "./util";
import Attachment from "./Attachment";

import { Box, Stack } from "@mui/material";

const ReadOnlyChatChime = () => {
  const { id: videoId } = useParams();
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(
      actions.setPageDetails({
        pageName: "View Chat",
        currentPage: "chat",
        menu: "entities",
      })
    );
  }, [dispatch]);

  const buildMessageFromChime = async (msg, cognitoId) => {
    const metadata = msg.Metadata ? JSON.parse(msg.Metadata) : undefined;
    if (metadata?.attachmentId) {
      await prepareChimeAttachment(metadata, cognitoId);
    }

    return {
      id: msg.MessageId,
      content: msg.Content,
      metadata,
      isOutbound: !!metadata?.avatarUrl,
    };
  };

  useEffect(() => {
    async function loadMessages() {
      try {
        const chimeMessagesResponse = await getVideoCallChatMessages(videoId);
        const chimeMessages = chimeMessagesResponse?.data?.messages || [];
        const cognitoId = chimeMessagesResponse?.data?.cognitoId;
        const chatHistory = [];
        for (const msg of chimeMessages) {
          const enrichedMessage = await buildMessageFromChime(msg, cognitoId);
          chatHistory.push(enrichedMessage);
        }
        setMessages(chatHistory);
      } catch (error) {
        console.error("Failed to load messages:", error);
      } finally {
        setLoading(false);
      }
    }
    if (videoId) loadMessages();
  }, [videoId]);

  const goBack = () => window.history.back();

  return (
    <PageContainer loading={loading}>
      <SectionContainer columnDirection>
        <Button variant="text" onClick={goBack} startIcon={<ArrowBackOutlinedIcon />}>
          Go Back
        </Button>
        <Grid container justifyContent="center" spacing={1}>
          <Grid item xs={2}>
            <Typography component="div">
              <strong>Chat Participants</strong>
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography component="div">
              {Array.from(
                new Set(
                  messages
                    .map((msg) => {
                      return msg.metadata?.fullName;
                    })
                    .filter(Boolean)
                )
              ).join(", ")}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography component="div">
              <strong>Total # of Messages</strong>
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Typography component="div">{messages.length}</Typography>
          </Grid>
        </Grid>
        <Stack pt="25px" pb="25px" width="100%">
          {messages.map((message) => {
            return (
              <Box
                key={message.MessageId}
                sx={{
                  textAlign: "left",
                  display: "flex",
                  justifyContent: message.metadata.avatarUrl ? "flex-start" : "flex-end",
                  marginBottom: 2,
                  scrollMarginTop: "70px",
                }}
              >
                <Box sx={{ display: "block", width: "40%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        width: "fit-content",
                        display: "block",
                        marginBottom: "5px",
                        fontSize: ".7rem",
                        "&:hover": {
                          cursor: "pointer",
                          fontWeight: 500,
                        },
                      }}
                    >
                      {message.metadata.fullName || "Unknown"}
                    </Typography>
                  </Box>
                  {message.metadata.attachmentUrl ? (
                    <Attachment msg={message} />
                  ) : (
                    <Typography
                      component="div"
                      sx={{
                        display: "block",
                        padding: "12px",
                        backgroundColor: message.metadata.avatarUrl ? "#999999" : "#2196f3",
                        color: "#fff",
                        borderRadius: "6px",
                      }}
                    >
                      {message.content}
                    </Typography>
                  )}
                  <Typography
                    component="div"
                    sx={{
                      width: "40%",
                      display: "block",
                      marginTop: "5px",
                      fontSize: ".7rem",
                      color: "#666",
                    }}
                  >
                    {format(
                      new Date(message.metadata.sentAt || message.CreatedTimestamp),
                      "MM/dd/yyyy h:mm a"
                    )}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </SectionContainer>
    </PageContainer>
  );
};

export default ReadOnlyChatChime;
